import NextImage from "next/image";
import NextLink from "next/link";

import { Button } from "@jetblue/kit/button";
import type { ButtonProperties } from "@jetblue/kit/button";
import { IcMosaicStatus } from "@jetblue/kit-icons/ic-mosaic-status";

import type { CustomerDetails } from "../../true-blue";

export type TrueBlueUserProperties = {
  customerDetails: CustomerDetails;
  loyalty: {
    cta: {
      href: string;
      isExternal: boolean;
      label: string;
      target?: "_blank" | "_self";
      variant?: ButtonProperties["variant"];
    };
    image?: string;
    imageAlt?: string;
    imageSrc?: unknown;
    labels: {
      greeting: string;
      membershipNumber: string;
      mosaic: string;
      points: string;
      pts: string;
      tiles: string;
      travelCredits: string;
    };
    mosaic: {
      theme: "dark" | "light";
      variant?: ButtonProperties["variant"];
    };
    theme: "dark" | "light";
  };
  isMosaic: boolean;
};
export const TrueBlueUser = ({
  customerDetails,
  isMosaic,
  loyalty,
}: TrueBlueUserProperties) => {
  const mosaicTier = customerDetails.loyaltyDetails.membershipTier.replaceAll(
    /\D+/g,
    "",
  );
  const loyaltyCTA = loyalty.cta.variant ?? "standard";
  const mosaicCTA = loyalty.mosaic.variant ?? "primary";

  return (
    <section
      className="bg-off-white p-6 text-core-blue data-[theme=dark]:bg-core-blue data-[theme=dark]:text-white"
      data-theme={isMosaic ? loyalty.mosaic.theme : loyalty.theme}
    >
      <div>
        <>
          {isMosaic ? (
            <div className="mb-3 flex items-center justify-center">
              <IcMosaicStatus aria-hidden className="mr-2 w-6" />
              <span className="text-legal">
                {loyalty.labels.mosaic} {mosaicTier}
              </span>
            </div>
          ) : (
            <>
              {loyalty.imageAlt && loyalty.image && (
                <div className="mb-3 flex h-6 items-center justify-center">
                  <NextImage
                    alt={loyalty.imageAlt}
                    height={8}
                    src={loyalty.image}
                    width={75}
                  />
                </div>
              )}
            </>
          )}
        </>

        <div className="mb-2 text-center text-body-extra-bold">
          {loyalty.labels.greeting}&nbsp;
          {customerDetails.firstName}
        </div>

        <div className="mb-3 text-center text-legal">
          {loyalty.labels.membershipNumber}
          {customerDetails.loyaltyDetails.membershipID}
        </div>
      </div>

      <div
        className="mb-4 flex justify-evenly text-charcoal data-[is-mosaic=true]:text-white"
        data-is-mosaic={isMosaic}
      >
        <div className="text-center">
          <div className="text-legal">{loyalty.labels.tiles}</div>
          <div className="text-body-extra-bold">
            {customerDetails.loyaltyDetails.tiles ?? 0}
          </div>
        </div>

        <div className="mt-1 inline-block h-9 w-px bg-medium-gray" />

        <div className="text-center">
          <div className="text-legal">{loyalty.labels.points}</div>
          <div className="text-body-extra-bold">
            {customerDetails.loyaltyDetails.accountBalance?.toLocaleString(
              "en",
            ) ?? 0}
          </div>
        </div>

        {Boolean(customerDetails.travelBankDetails?.balance?.amount) && (
          <>
            <div className="mt-1 inline-block h-9 w-px bg-medium-gray" />

            <div className="text-center">
              <div className="text-center text-legal">
                <div className="text-legal">{loyalty.labels.travelCredits}</div>

                <div className="text-body-extra-bold">
                  {customerDetails.travelBankDetails?.balance?.amount}
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {loyalty.cta.href && loyalty.cta.label && (
        <Button
          as={NextLink}
          href={loyalty.cta.href}
          isFullWidth
          size="small"
          target={loyalty.cta.target}
          variant={isMosaic ? mosaicCTA : loyaltyCTA}
        >
          {loyalty.cta.label}
        </Button>
      )}
    </section>
  );
};
